// src/utils/migrations/repairSafeOperationalAccessProfiles.js

import {
  doc,
  getDoc,
} from "firebase/firestore";

import { db } from "../../firebase";

import auditOperationalAccessProfiles from "./auditOperationalAccessProfiles";

import {
  finalizeFacilityMembership,
} from "../../finalizeFacilityMembership";

import {
  PLATFORM_ROLES,
  normalizeId,
  normalizePlatformRole,
} from "../../security/permissions";

/*
 * =========================================================
 * SHIFTBRIDGE SAFE OPERATIONAL ACCESS REPAIR
 * =========================================================
 *
 * IMPORTANT
 * ---------
 * This migration repairs ONLY accounts classified by the
 * read-only audit as:
 *
 *   SAFE_AUTO_REPAIR
 *
 * It will NOT repair:
 *
 *   BLOCKED_SOURCE_DATA
 *   MANUAL_IDENTITY_REVIEW
 *   NOT_OPERATIONALLY_READY
 *
 * Before every write it re-reads users/{uid} and validates
 * the source identity again.
 */

const REPAIRABLE_ROLES = new Set([
  PLATFORM_ROLES.FACILITY_PLANNER,
  PLATFORM_ROLES.FACILITY_CLIENT,
  PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
  PLATFORM_ROLES.WORKER,
]);

function uniqueIds(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map(normalizeId)
        .filter(Boolean)
        .filter(
          (value) => value !== "all"
        )
    )
  ).sort();
}

function getUserFacilityIds(
  user = {}
) {
  return uniqueIds([
    user?.primaryFacilityId,
    user?.facilityId,

    ...(Array.isArray(
      user?.facilityIds
    )
      ? user.facilityIds
      : []),

    ...(Array.isArray(
      user?.allowedFacilityIds
    )
      ? user.allowedFacilityIds
      : []),
  ]);
}

function getPrimaryFacilityId(
  user = {},
  facilityIds = []
) {
  const preferred =
    normalizeId(
      user?.primaryFacilityId ||
        user?.facilityId ||
        ""
    );

  if (
    preferred &&
    preferred !== "all"
  ) {
    return preferred;
  }

  return facilityIds[0] || "";
}

function getUserRole(user = {}) {
  return normalizePlatformRole(
    user?.platformRole ||
      user?.role ||
      ""
  );
}

function isWorkerOperationallyReady(
  user = {}
) {
  const status =
    normalizeId(user?.status);

  const onboardingStatus =
    normalizeId(
      user?.onboardingStatus
    );

  const complianceStatus =
    normalizeId(
      user?.complianceStatus
    );

  const workerStatus =
    normalizeId(
      user?.workerStatus
    );

  const operationalState =
    String(
      user?.workerOperationalState ||
        ""
    )
      .trim()
      .toUpperCase();

  return (
    status === "active" &&
    (
      onboardingStatus === "approved" ||
      user?.onboardingCompleted === true
    ) &&
    (
      complianceStatus === "approved" ||
      complianceStatus === "ready"
    ) &&
    (
      workerStatus === "ready" ||
      operationalState === "READY"
    )
  );
}

function assertStillSafe({
  row,
  user,
  uid,
}) {
  const email =
    String(
      user?.email || ""
    )
      .trim()
      .toLowerCase();

  const role =
    getUserRole(user);

  const tenantId =
    normalizeId(
      user?.tenantId
    );

  const facilityIds =
    getUserFacilityIds(user);

  const primaryFacilityId =
    getPrimaryFacilityId(
      user,
      facilityIds
    );

  if (!email) {
    throw new Error(
      `Repair blocked for ${uid}: missing email.`
    );
  }

  if (
    !REPAIRABLE_ROLES.has(role)
  ) {
    throw new Error(
      `Repair blocked for ${uid}: unsupported role "${role}".`
    );
  }

  if (
    role !== row.role
  ) {
    throw new Error(
      `Repair blocked for ${uid}: role changed since audit.`
    );
  }

  if (
    !tenantId ||
    tenantId === "all"
  ) {
    throw new Error(
      `Repair blocked for ${uid}: tenantId is missing or invalid.`
    );
  }

  if (
    tenantId !== row.tenantId
  ) {
    throw new Error(
      `Repair blocked for ${uid}: tenant changed since audit.`
    );
  }

  if (
    facilityIds.length === 0 ||
    !primaryFacilityId
  ) {
    throw new Error(
      `Repair blocked for ${uid}: facility scope is missing.`
    );
  }

  if (
    role === PLATFORM_ROLES.WORKER &&
    !isWorkerOperationallyReady(user)
  ) {
    throw new Error(
      `Repair blocked for ${uid}: worker is no longer operationally ready.`
    );
  }

  if (
    role === PLATFORM_ROLES.WORKER &&
    user?.credentials &&
    typeof user.credentials ===
      "object" &&
    !Array.isArray(
      user.credentials
    )
  ) {
    throw new Error(
      `Repair blocked for ${uid}: worker credentials are still stored as a legacy map.`
    );
  }

  return {
    email,
    role,
    tenantId,
    facilityIds,
    primaryFacilityId,
  };
}

export default async function
repairSafeOperationalAccessProfiles(scope = {}) {
  console.log(
    "SHIFTBRIDGE SAFE ACCESS REPAIR — PRE-AUDIT START"
  );

  /*
   * Always regenerate the audit immediately
   * before modifying anything.
   */
  const before =
    await auditOperationalAccessProfiles(scope);

  const safeRows =
    (before?.rows || []).filter(
      (row) =>
        row?.repairDisposition ===
        "SAFE_AUTO_REPAIR"
    );

  console.log(
    "SHIFTBRIDGE SAFE ACCESS REPAIR — CANDIDATES",
    safeRows.map((row) => ({
      uid: row.uid,
      email: row.email,
      role: row.role,
      tenantId: row.tenantId,
      facilityId:
        row.primaryFacilityId,
      issues: row.issues,
    }))
  );

  const results = [];

  for (const row of safeRows) {
    const uid =
      String(row?.uid || "").trim();

    try {
      if (!uid) {
        throw new Error(
          "Safe-repair row is missing uid."
        );
      }

      /*
       * Re-read users/{uid}.
       *
       * We do not trust stale audit data for
       * the actual write.
       */
      const userRef =
        doc(
          db,
          "users",
          uid
        );

      const userSnap =
        await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error(
          `users/${uid} no longer exists.`
        );
      }

      const user =
        userSnap.data() || {};

      const {
        email,
        role,
        tenantId,
        facilityIds,
        primaryFacilityId,
      } = assertStillSafe({
        row,
        user,
        uid,
      });

      console.log(
        "SHIFTBRIDGE SAFE ACCESS REPAIR — START",
        {
          uid,
          email,
          role,
          tenantId,
          primaryFacilityId,
          facilityIds,
        }
      );

      /*
       * Canonical finalizer performs the deeper
       * identity, tenant and facility validation.
       *
       * It also:
       *
       * - creates missing accessProfiles/{uid}
       * - replaces stale permission maps
       * - canonicalizes schema versions
       * - repairs facility scope
       * - rebuilds planner/worker roster mirrors
       */
      const repairResult =
        await finalizeFacilityMembership({
          uid,

          email,

          role,
          platformRole:
            role,

          tenantId,

          status:
            String(
              user?.status ||
                "active"
            )
              .trim()
              .toLowerCase(),

          facilityId:
            primaryFacilityId,

          primaryFacilityId,

          facilityIds,

          allowedFacilityIds:
            facilityIds,

          credentials:
            Array.isArray(
              user?.credentials
            )
              ? user.credentials
              : [],

          disabled:
            false,

          /*
           * Do not manufacture an invitation.
           */
          inviteId:
            "",

          /*
           * Explicit administrative migration
           * provenance.
           */
          source:
            "admin_operational_access_repair",
        });

      results.push({
        ok: true,
        uid,
        email,
        role,
        tenantId,
        primaryFacilityId,
        facilityIds,
        repairedIssues:
          row?.issues || [],
        result:
          repairResult,
      });

      console.log(
        "SHIFTBRIDGE SAFE ACCESS REPAIR — SUCCESS",
        {
          uid,
          email,
          role,
          tenantId,
          primaryFacilityId,
        }
      );
    } catch (error) {
      const failure = {
        ok: false,

        uid:
          row?.uid || "",

        email:
          row?.email || "",

        role:
          row?.role || "",

        code:
          error?.code || "",

        message:
          error?.message ||
          String(error),
      };

      results.push(failure);

      console.error(
        "SHIFTBRIDGE SAFE ACCESS REPAIR — FAILED",
        failure
      );
    }
  }

  /*
   * Verify the database after the repair.
   */
  console.log(
    "SHIFTBRIDGE SAFE ACCESS REPAIR — POST-AUDIT START"
  );

  const after =
    await auditOperationalAccessProfiles(scope);

  const repaired =
    results.filter(
      (item) => item.ok
    ).length;

  const failed =
    results.filter(
      (item) => !item.ok
    ).length;

  const report = {
    attempted:
      safeRows.length,

    repaired,

    failed,

    before:
      before?.summary || null,

    after:
      after?.summary || null,

    results,
  };

  console.log(
    "SHIFTBRIDGE SAFE ACCESS REPAIR — COMPLETE",
    report
  );

  return report;
}